import type { VehicleChassisDefinition, VehicleWeaponDefinition } from "@/types/game-data";

// --------------------------------------------------
// MILITIA VEHICLES
// --------------------------------------------------
export const MILITIA_VEHICLES: readonly VehicleChassisDefinition[] = [
	{
		id: "militia-technical", name: "Technical", faction: "militia", baseCost: 75, category: "improvised",
		mountSlots: [{ id: "main-mount", allowedTypes: ["light", "medium", "heavy"], required: false }],
		movement: 8, vehicleClass: "Civilian 1", armourValue: 3, transport: 4,
		vehicleSpecialRules: ["Makeshift Transport 8", "All Terrain Modifications"],
	},
	{
		id: "militia-armoured-pickup", name: "Armoured Pickup/SUV", faction: "militia", baseCost: 100, category: "improvised",
		mountSlots: [{ id: "main-mount", allowedTypes: ["light", "medium", "heavy"], required: false }],
		movement: 7, vehicleClass: "Civilian 2", armourValue: 5, transport: 4,
		vehicleSpecialRules: ["All Terrain Modifications"],
	},
	{
		id: "militia-mrap", name: "MRAP", faction: "militia", baseCost: 200, category: "improvised",
		mountSlots: [
			{ id: "mount-1", allowedTypes: ["light", "medium"], required: false },
			{ id: "mount-2", allowedTypes: ["light", "medium"], required: false },
		],
		movement: 7, vehicleClass: "Medium 1", armourValue: 5, transport: 6,
		vehicleSpecialRules: ["Mine Protection"],
	},
	{
		id: "militia-apc-tracked", name: "APC, Tracked", faction: "militia", baseCost: 120, category: "captured",
		mountSlots: [{ id: "main-mount", allowedTypes: ["light", "medium"], required: false }],
		includedWeapons: ["vw-spg9-heat"], notes: "Includes SPG9",
		movement: 5, vehicleClass: "Light 2", armourValue: 5, transport: 8,
		vehicleSpecialRules: ["Tracked", "Makeshift Transport 10"],
	},
	{
		id: "militia-bmp1", name: "BMP-1", faction: "militia", baseCost: 120, category: "captured",
		mountSlots: [], includedWeapons: ["vw-zpu1", "vw-mmg"],
		notes: "Wheeled. Includes ZPU-1 and MMG",
		movement: 5, vehicleClass: "Light 2", armourValue: 5, transport: 8,
		vehicleSpecialRules: ["Makeshift Transport 10", "Tracked"],
	},
	{
		id: "militia-bmp2", name: "BMP-2", faction: "militia", baseCost: 200, category: "captured",
		mountSlots: [], includedWeapons: ["vw-medium-cannon", "vw-atgm", "vw-mmg"],
		notes: "Tracked. Includes Medium Cannon, ATGM, MMG",
		movement: 5, vehicleClass: "Light 2", armourValue: 6, transport: 7,
		vehicleSpecialRules: ["Makeshift Transport 10", "Tracked"],
	},
	{
		id: "militia-btr80", name: "BTR-80", faction: "militia", baseCost: 250, category: "captured",
		mountSlots: [], notes: "Wheeled",
		movement: 6, vehicleClass: "Medium 1", armourValue: 5, transport: 7,
		vehicleSpecialRules: ["Makeshift Transport 8"],
	},
	{
		id: "militia-t55", name: "T-55 (Cheap Light Tank)", faction: "militia", baseCost: 120, category: "captured",
		mountSlots: [], includedWeapons: ["vw-light-tank-gun", "vw-hmg"],
		notes: "Includes Light Tank Gun and HMG",
		movement: 5, vehicleClass: "Heavy 3", armourValue: 7, transport: 0,
		vehicleSpecialRules: ["Tracked"],
	},
	{
		id: "militia-t72", name: "T-72", faction: "militia", baseCost: 350, category: "captured",
		mountSlots: [], includedWeapons: ["vw-120-125mm-gun", "vw-hmg"],
		notes: "Stabilised Gun with 120-125mm gun and HMG. Basic ERA, APS",
		movement: 5, vehicleClass: "Heavy 3", armourValue: 7, transport: 0,
		vehicleSpecialRules: ["Tracked", "1st Generation APS", "ERA"],
	},
	{
		id: "militia-t90", name: "T-90", faction: "militia", baseCost: 500, category: "captured",
		mountSlots: [], includedWeapons: ["vw-120-125mm-gun", "vw-hmg"],
		notes: "Stabilised Gun with 120-125mm gun and HMG. High-end, APS",
		movement: 5, vehicleClass: "Heavy 4", armourValue: 8, transport: 0,
		vehicleSpecialRules: ["Tracked", "Next Generation ERA", "Advanced APS"],
	},
] as const;

// --------------------------------------------------
// TASK FORCE VEHICLES
// --------------------------------------------------
export const TASKFORCE_VEHICLES: readonly VehicleChassisDefinition[] = [
	{
		id: "tf-technical", name: "Technical", faction: "taskforce", baseCost: 75, category: "tactical",
		mountSlots: [{ id: "main-mount", allowedTypes: ["light", "medium", "heavy"], required: false }],
		movement: 8, vehicleClass: "Civilian 1", armourValue: 3, transport: 4,
		vehicleSpecialRules: ["Makeshift Transport 8", "All Terrain Modifications"],
	},
	{
		id: "tf-hmv", name: "HMV", faction: "taskforce", baseCost: 120, category: "tactical",
		mountSlots: [
			{ id: "mount-1", allowedTypes: ["light", "medium", "heavy", "rws-light"], required: false },
			{ id: "mount-2", allowedTypes: ["light", "medium", "heavy", "rws-light"], required: false },
		],
		movement: 7, vehicleClass: "Light 1", armourValue: 5, transport: 6,
	},
	{
		id: "tf-armoured-pickup", name: "Armoured Pickup/SUV", faction: "taskforce", baseCost: 100, category: "tactical",
		mountSlots: [{ id: "main-mount", allowedTypes: ["light", "medium", "heavy", "rws-light"], required: false }],
		movement: 7, vehicleClass: "Civilian 2", armourValue: 5, transport: 4,
		vehicleSpecialRules: ["All Terrain Modifications"],
	},
	{
		id: "tf-dagger", name: "Dagger", faction: "taskforce", baseCost: 120, category: "tactical",
		mountSlots: [
			{ id: "mount-1", allowedTypes: ["light", "medium"], required: false },
			{ id: "mount-system", allowedTypes: ["rws-light", "rws-medium", "rws-heavy"], required: false },
		],
		movement: 10, vehicleClass: "Light 1", armourValue: 4, transport: 4,
		vehicleSpecialRules: ["Ultralight", "All Terrain Modifications"],
	},
	{
		id: "tf-mrap", name: "MRAP", faction: "taskforce", baseCost: 200, category: "armoured",
		mountSlots: [
			{ id: "mount-1", allowedTypes: ["light", "medium", "heavy"], required: false },
			{ id: "mount-2", allowedTypes: ["light", "medium", "heavy"], required: false },
			{ id: "mount-system", allowedTypes: ["rws-light", "rws-medium", "rws-heavy"], required: false },
		],
		notes: "+20r to base MRAP for mount system",
		movement: 7, vehicleClass: "Medium 1", armourValue: 5, transport: 6,
		vehicleSpecialRules: ["Mine Protection"],
	},
] as const;

// --------------------------------------------------
// RUSSIAN VEHICLES
// --------------------------------------------------
export const RUSSIAN_VEHICLES: readonly VehicleChassisDefinition[] = [
	{
		id: "ru-btr80", name: "BTR-80", faction: "russian", baseCost: 250, category: "armoured",
		mountSlots: [], includedWeapons: ["vw-light-cannon", "vw-mmg"],
		notes: "Turret mounted Light Cannon, Medium Machine Gun, and Smoke Launcher. Makeshift Transport 8.",
		movement: 6, vehicleClass: "Medium 1", armourValue: 5, transport: 7,
		vehicleSpecialRules: ["Makeshift Transport 8"],
	},
	{
		id: "ru-btr82", name: "BTR-82", faction: "russian", baseCost: 275, category: "armoured",
		mountSlots: [{ id: "turret-upgrade", allowedTypes: ["heavy"], required: false }],
		includedWeapons: ["vw-medium-cannon", "vw-mmg"],
		notes: "Remote Turret with Medium Cannon and MMG. MK1 Night Fighting, Enhanced Crew Protection, NBC Filtration. May upgrade turret: stabilised Medium Cannon + MMG + 2 guided AT launchers + smoke + MK2 Night Fighting (+120r), OR stabilised Heavy Cannon + MMG + smoke + MK2 Night Fighting (+100r). May add Advanced Comms (+10r).",
		movement: 6, vehicleClass: "Medium 1", armourValue: 6, transport: 7,
		vehicleSpecialRules: ["Makeshift Transport 8", "MK1 Night Fighting", "Enhanced Crew Protection", "NBC Filtration"],
	},
] as const;

// --------------------------------------------------
// LAW ENFORCEMENT VEHICLES
// --------------------------------------------------
export const LAW_ENFORCEMENT_VEHICLES: readonly VehicleChassisDefinition[] = [
	{
		id: "le-patrol-vehicle", name: "Patrol Vehicle", faction: "law-enforcement", baseCost: 50, category: "civilian",
		mountSlots: [],
		notes: "Represents most patrol vehicles: marked or undercover cars, SUVs, pickup trucks.",
		movement: 8, vehicleClass: "Civilian 1", armourValue: 3, transport: 4,
	},
	{
		id: "le-bearcat", name: "Bearcat", faction: "law-enforcement", baseCost: 150, category: "armoured",
		mountSlots: [],
		notes: "Armoured SWAT vehicle.",
		movement: 9, vehicleClass: "Light 1", armourValue: 6, transport: 10,
		vehicleSpecialRules: ["Makeshift Transport 4", "All Terrain Modifications", "Mine Protection"],
	},
] as const;

// --------------------------------------------------
// CRIMINAL ELEMENT VEHICLES
// --------------------------------------------------
export const CRIMINAL_ELEMENT_VEHICLES: readonly VehicleChassisDefinition[] = [
	{
		id: "ce-civilian-vehicle", name: "Civilian Vehicle", faction: "criminal-element", baseCost: 30, category: "civilian",
		mountSlots: [],
		notes: "Any civilian vehicle that is part of the wider criminal gang.",
		movement: 8, vehicleClass: "Civilian 1", armourValue: 3, transport: 4,
	},
] as const;

export const ALL_VEHICLE_CHASSIS: readonly VehicleChassisDefinition[] = [
	...MILITIA_VEHICLES,
	...TASKFORCE_VEHICLES,
	...RUSSIAN_VEHICLES,
	...LAW_ENFORCEMENT_VEHICLES,
	...CRIMINAL_ELEMENT_VEHICLES,
] as const;

// --------------------------------------------------
// VEHICLE WEAPON MOUNTS
// --------------------------------------------------
export const VEHICLE_WEAPONS: readonly VehicleWeaponDefinition[] = [
	// Light Weapons
	{
		id: "vw-lmg", name: "Light Machine Gun", mountType: "light", cost: 5,
		stats: {
			cqb: { range: '6"', modifier: 0, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "5+", ap: 1 },
			extreme: { range: '40"', modifier: -1, lethality: "6+", ap: 1 },
			specialRules: ["Automatic", "Suppressing"],
		},
	},
	{
		id: "vw-assault-mg", name: "Assault Machine Gun", mountType: "light", cost: 1,
		stats: {
			cqb: { range: '6"', modifier: 1, lethality: "5+", ap: 1 },
			engagement: { range: '15"', modifier: 1, lethality: "6+", ap: 1 },
			extreme: { range: '25"', modifier: -1, lethality: "7+", ap: 0 },
			specialRules: ["Automatic", "Compact"],
		},
	},
	{
		id: "vw-microgun", name: "Microgun", mountType: "light", cost: 30,
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "7+", ap: 1 },
			extreme: { range: '40"', modifier: 0, lethality: "8+", ap: 1 },
			specialRules: ["Sustained 3", "Suppressing"],
		},
	},

	// Medium Weapons
	{
		id: "vw-mmg", name: "Medium Machine Gun", mountType: "medium", cost: 10,
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '30"', modifier: 1, lethality: "5+", ap: 2 },
			extreme: { range: '50"', modifier: 0, lethality: "5+", ap: 1 },
			specialRules: ["Sustained 2", "Suppressing"],
		},
	},
	{
		id: "vw-dual-mmg", name: "Dual Medium Machine Gun", mountType: "medium", cost: 20,
		stats: {
			cqb: { range: '6"', modifier: -1, lethality: "7+", ap: 1 },
			engagement: { range: '30"', modifier: 1, lethality: "5+", ap: 2 },
			extreme: { range: '50"', modifier: 0, lethality: "5+", ap: 1 },
			specialRules: ["Sustained 3", "Suppressing"],
		},
	},
	{
		id: "vw-manpad", name: "MANPAD", mountType: "medium", cost: 60,
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: null, lethality: "4+", ap: 6 },
			extreme: { range: "∞", modifier: null, lethality: "4+", ap: 6 },
			specialRules: ["Anti-Air", "Guided", "One Shot"],
		},
	},
	{
		id: "vw-medium-cannon", name: "Medium Cannon", mountType: "medium", cost: 50,
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: 0, lethality: "5+", ap: 3 },
			extreme: { range: "∞", modifier: -1, lethality: "5+", ap: 3 },
			specialRules: ["Anti Armour", "Sustained 2", "Suppressing"],
		},
	},
	{
		id: "vw-auto-grenade-launcher", name: "Automatic Grenade Launcher", mountType: "medium", cost: 35,
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '20"', modifier: -1, lethality: "6+", ap: 1 },
			extreme: { range: '40"', modifier: -2, lethality: "6+", ap: 1 },
			specialRules: ["AoE 3", "Automatic", "Frag", "Suppressing", "Armour Piercing"],
		},
	},

	// Heavy Weapons
	{
		id: "vw-hmg", name: "Heavy Machine Gun", mountType: "heavy", cost: 20,
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "5+", ap: 2 },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Sustained 2", "Suppressing", "Armour Piercing"],
		},
	},
	{
		id: "vw-spg9-heat", name: "SPG9 HEAT/SPG9 HE", mountType: "heavy", cost: 40, notes: "Militia",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: 0, lethality: "7+", ap: 2 },
			extreme: { range: '60"', modifier: -1, lethality: "7+", ap: 2 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire"],
		},
	},
	{
		id: "vw-m40-heat", name: "M40 HEAT", mountType: "heavy", cost: 75, notes: "Militia",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '45"', modifier: 0, lethality: "7+", ap: 2 },
			extreme: { range: "∞", modifier: -2, lethality: "7+", ap: 3 },
			specialRules: ["AoE 2", "Anti Armour", "Single Fire"],
		},
	},
	{
		id: "vw-light-guided-at", name: "Light Guided Anti-Tank Launcher", mountType: "heavy", cost: 90,
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '35"', modifier: null, lethality: "5+", ap: 8 },
			extreme: { range: '60"', modifier: null, lethality: "5+", ap: 8 },
			specialRules: ["AoE 2", "Anti Tank", "One Shot", "Tandem Warhead", "Guided", "Top Down"],
		},
	},
	{
		id: "vw-zpu1", name: "ZPU-1", mountType: "heavy", cost: 50, notes: "Militia",
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "5+", ap: 2 },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Sustained 2", "Suppressing", "Armour Piercing", "Anti-Air"],
		},
	},
	{
		id: "vw-zpu2", name: "ZPU-2", mountType: "heavy", cost: 75, notes: "Militia",
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "5+", ap: 2 },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Sustained 3", "Suppressing", "Armour Piercing", "Anti-Air"],
		},
	},
	{
		id: "vw-zpu4", name: "ZPU-4", mountType: "heavy", cost: 90, notes: "Militia",
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "5+", ap: 2 },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Sustained 4", "Suppressing", "Armour Piercing", "Anti-Air"],
		},
	},
	{
		id: "vw-zsu23", name: "ZSU-23", mountType: "heavy", cost: 110, notes: "Militia",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 3 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 3 },
			specialRules: ["Sustained 3", "Anti Armour", "Anti-Air", "Suppressing"],
		},
	},
	{
		id: "vw-rocket-pod", name: "Rocket Pod", mountType: "heavy", cost: 90, notes: "Militia",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: -1, lethality: "4+", ap: 2 },
			extreme: { range: '60"', modifier: -2, lethality: "4+", ap: 2 },
			specialRules: ["AoE 5", "Single Fire", "Frag", "Suppressing"],
		},
	},
	{
		id: "vw-light-cannon", name: "Light Cannon", mountType: "heavy", cost: 130,
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: 0, lethality: "5+", ap: 2 },
			extreme: { range: "∞", modifier: -1, lethality: "5+", ap: 2 },
			specialRules: ["Anti Armour", "Sustained 2", "Suppressing"],
		},
	},
	{
		id: "vw-vulcan-cannon", name: "Vulcan Cannon", mountType: "heavy", cost: 150, notes: "Militia",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 3 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 3 },
			specialRules: ["Sustained 4", "Anti Armour", "Anti-Air", "Suppressing"],
		},
	},
	{
		id: "vw-heavy-cannon", name: "Heavy Cannon", mountType: "heavy", cost: 75,
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '50"', modifier: 0, lethality: "4+", ap: 4 },
			extreme: { range: "∞", modifier: -1, lethality: "4+", ap: 4 },
			specialRules: ["Anti Armour", "Sustained 2", "Suppressing"],
		},
	},
	{
		id: "vw-heavy-chaingun", name: "Heavy Chaingun", mountType: "heavy", cost: 100,
		stats: {
			cqb: { range: '6"', modifier: -2, lethality: "5+", ap: 2 },
			engagement: { range: '50"', modifier: 1, lethality: "3+", ap: 2 },
			extreme: { range: "∞", modifier: 0, lethality: "3+", ap: 2 },
			specialRules: ["Sustained 3", "Suppressing", "Armour Piercing"],
		},
	},
	{
		id: "vw-minigun", name: "Minigun", mountType: "heavy", cost: 30,
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "6+", ap: 1 },
			engagement: { range: '20"', modifier: 1, lethality: "7+", ap: 1 },
			extreme: { range: '40"', modifier: 0, lethality: "8+", ap: 1 },
			specialRules: ["Sustained 3", "Suppressing"],
		},
	},
	{
		id: "vw-heavy-minigun", name: "Heavy Minigun", mountType: "heavy", cost: 30,
		stats: {
			cqb: { range: '6"', modifier: 2, lethality: "5+", ap: 2 },
			engagement: { range: '30"', modifier: 1, lethality: "4+", ap: 2 },
			extreme: { range: '50"', modifier: 0, lethality: "4+", ap: 2 },
			specialRules: ["Sustained 4", "Suppressing", "Armour Piercing"],
		},
	},

	// Tank Guns & ATGM (included weapons on captured vehicles)
	{
		id: "vw-light-tank-gun", name: "Light Tank Gun (100mm)", mountType: "heavy", cost: 0,
		notes: "Included with T-55",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '50"', modifier: 0, lethality: "4+", ap: 4 },
			extreme: { range: "∞", modifier: -1, lethality: "4+", ap: 4 },
			specialRules: ["Anti Armour", "Single Fire", "AoE 3"],
		},
	},
	{
		id: "vw-120-125mm-gun", name: "120-125mm Tank Gun", mountType: "heavy", cost: 0,
		notes: "Included with T-72/T-90",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '60"', modifier: 0, lethality: "3+", ap: 6 },
			extreme: { range: "∞", modifier: -1, lethality: "3+", ap: 6 },
			specialRules: ["Anti Tank", "Single Fire", "AoE 3", "Stabilised"],
		},
	},
	{
		id: "vw-atgm", name: "ATGM", mountType: "heavy", cost: 0,
		notes: "Included with BMP-2",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: null, lethality: "5+", ap: 6 },
			extreme: { range: "∞", modifier: null, lethality: "5+", ap: 6 },
			specialRules: ["Anti Tank", "Guided", "Single Fire", "Tandem Warhead", "Minimum Range 10\""],
		},
	},

	// Special/Heavy Weapons (for mount systems)
	{
		id: "vw-javelin", name: "Javelin", mountType: "special", cost: 80, notes: "Task Force",
		stats: {
			cqb: { range: "-", modifier: null, lethality: null, ap: null },
			engagement: { range: '40"', modifier: null, lethality: "5+", ap: 8 },
			extreme: { range: "∞", modifier: null, lethality: "5+", ap: 8 },
			specialRules: ["AoE 2", "Anti Tank", "One Shot", "Tandem Warhead", "Guided", "Top Down"],
		},
	},

	// RWS (Remote Weapon Systems) — Task Force
	{
		id: "vw-rws-light", name: "Light RWS", mountType: "rws-light", cost: 15,
		notes: "Choose one Light weapon",
	},
	{
		id: "vw-rws-medium", name: "Medium RWS", mountType: "rws-medium", cost: 15,
		notes: "Choose one Medium or Heavy weapon",
	},
	{
		id: "vw-rws-heavy", name: "Heavy RWS", mountType: "rws-heavy", cost: 15,
		notes: "Choose one Heavy + optional Medium/Heavy + optional Special weapon",
	},
] as const;

/** Lookup vehicle chassis by ID */
export function getVehicleChassisById(id: string): VehicleChassisDefinition | undefined {
	return ALL_VEHICLE_CHASSIS.find((v) => v.id === id);
}

/** Get vehicle weapons by mount type */
export function getVehicleWeaponsByMountType(
	mountType: string,
): readonly VehicleWeaponDefinition[] {
	return VEHICLE_WEAPONS.filter((w) => w.mountType === mountType);
}
